<template>
  <SettingsPagePanelHeader>
    <template #title>
      <div v-if="isEditPage">チーム編集</div>
      <div v-else>チーム詳細</div>
    </template>
    <template #right>
      <div v-if="isEditPage">
        <FlatButton :disabled="isButtonDisabled" @click="updateTeam">
          保存
        </FlatButton>
      </div>
      <div v-else-if="team">
        <RouterLink
          :to="{
            name: 'teamManagementSettingEdit',
            params: {
              teamManagementSettingId: team.guid,
              entranceGUID: entranceGUID,
            },
          }"
        >
          <FlatButton :class="$style.button" :disabled="isLoading">
            <template #icon>
              <IconPen :class="[$style.button__icon]" />
            </template>
            編集
          </FlatButton>
        </RouterLink>
      </div>
    </template>
  </SettingsPagePanelHeader>
  <SettingsTable :class="$style.table">
    <SettingsTableRow name="チーム名">
      <div v-if="isEditPage">
        <DolphinTextInput
          type="text"
          v-model="form.name"
          :class="$style.textInput"
          :disabled="isButtonDisabled"
        />
      </div>
      <div v-else>
        <p :class="$style.text">{{ team?.name }}</p>
      </div>
    </SettingsTableRow>
    <SettingsTableRow name="メンバー">
      <div v-if="isEditPage" :class="$style.listHeader">
        <DolphinSelector
          :class="$style.listHeader__selector"
          :options="staffOptions"
          placeholder="メンバーを選択"
          v-model="selectedGUID"
        />
        <div :class="$style.listHeader__buttons">
          <FlatButton :disabled="isAddStaffDisabled" @click="addStaff">
            追加
          </FlatButton>
          <FlatButton
            :disabled="isDeleteStaffDisabled"
            @click="deleteStaff"
            variant="caution"
          >
            削除
          </FlatButton>
        </div>
      </div>
      <div v-if="isLoading" :class="$style.loading">読み込み中...</div>
      <div v-else-if="team && form.staffs.length === 0">
        <p :class="{ [$style.empty]: isEditPage }">メンバーがいません</p>
      </div>
      <div v-else-if="team">
        <ul :class="$style.list">
          <li
            v-for="staff in form.staffs"
            :key="staff.guid"
            :class="$style.list__item"
          >
            <div :class="$style.listItem">
              <div v-if="isEditPage">
                <DolphinCheckbox
                  :modelValue="!!staff.checked"
                  @update:model-value="
                    (checked: boolean) => handleChecking(staff.guid, checked)
                  "
                  :disabled="isButtonDisabled"
                />
              </div>
              <p>{{ staff.displayName }}</p>
            </div>
          </li>
        </ul>
      </div>
      <div v-else :class="$style.empty">チーム情報が取得できませんでした</div>
    </SettingsTableRow>
  </SettingsTable>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import DolphinCheckbox from '@/components/atoms/DolphinCheckbox.vue'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import DolphinSelector from '@/components/atoms/DolphinSelector.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import IconPen from '@/components/icons/IconPen.vue'
import SettingsPagePanelHeader from '@/pages/SettingsPage/SettingsPagePanelHeader.vue'
import SettingsTableRow from '@/pages/SettingsPage/SettingsTable/SettingsTableRow.vue'
import SettingsTable from '@/pages/SettingsPage/SettingsTable/SettingsTable.vue'
import { useEntranceGUIDParam } from '@/features/entrance/useEntranceGUIDParam'
import { useAlert } from '@/libs/alertPortal'
import { useTeamManagementFormQuery } from './TeamManagementForm.generated'

const { result, loading } = useTeamManagementFormQuery()

type Staff = {
  guid: string
  displayName: string
  checked?: boolean
}

type Team = {
  guid: string
  name: string
  staffs: Staff[]
}

const props = defineProps<{
  team: Team | undefined
  isLoading: boolean
  isSubmitting: boolean
  isEditPage?: boolean
}>()

// TeamManagementForm は複数のルートで使用されるため、entranceGUID の取得にあたって useRoute ではなく useEntranceGUIDParam を使用する
const { entranceGUID } = useEntranceGUIDParam({ must: true })

const team = computed(() => props.team)
const isLoading = computed(() => props.isLoading || loading.value)
const isSubmitting = computed(() => props.isSubmitting)
const isButtonDisabled = computed(() => isLoading.value || isSubmitting.value)
const isEditPage = computed(() => props.isEditPage)
const staffOptions = computed(() => {
  if (!result.value) {
    return []
  }
  return result.value.staffsByPlanner.map(staff => ({
    value: staff.guid,
    text: staff.displayName,
  }))
})

const emmit = defineEmits<{
  (e: 'updateTeam', team: Team): void
}>()

const form = ref({
  guid: '',
  name: '',
  staffs: [] as Staff[],
})

const initForm = () => {
  if (!team.value) {
    return
  }

  form.value = {
    guid: team.value.guid,
    name: team.value.name,
    staffs: team.value.staffs.map(staff => ({
      guid: staff.guid,
      displayName: staff.displayName,
      checked: false,
    })),
  }
}

watch(
  team,
  () => {
    initForm()
  },
  { immediate: true }
)

const selectedGUID = ref<string | undefined>(undefined)

const isDeleteStaffDisabled = computed(() => {
  return (
    form.value.staffs.length === 0 ||
    form.value.staffs.every(staff => staff.checked === false) ||
    isButtonDisabled.value
  )
})

const isAddStaffDisabled = computed(() => {
  return !selectedGUID.value || isButtonDisabled.value
})

const deleteStaff = () => {
  if (isSubmitting.value || isDeleteStaffDisabled.value) {
    return
  }

  if (!form.value) {
    alert.error('チームの情報が取得できませんでした')
    return
  }

  form.value.staffs = form.value.staffs.filter(staff => {
    return staff.checked === false
  })
}

const addStaff = () => {
  if (isSubmitting.value || isAddStaffDisabled.value) {
    return
  }

  if (!selectedGUID.value) {
    alert.error('メンバーを選択してください')
    return
  }

  const selectedStaff = staffOptions.value.find(
    staff => staff.value === selectedGUID.value
  )
  if (!selectedStaff) {
    alert.error('メンバーが見つかりませんでした')
    return
  }

  form.value.staffs = [
    ...form.value.staffs.filter(staff => staff.guid !== selectedStaff.value),
    {
      guid: selectedStaff.value,
      displayName: selectedStaff.text,
      checked: false,
    },
  ]

  selectedGUID.value = undefined
}

const handleChecking = (guid: string, checked: boolean) => {
  console.log('handleChecking', guid, checked)
  form.value.staffs = form.value.staffs.map(staff => {
    if (staff.guid === guid) {
      return {
        ...staff,
        checked,
      }
    }
    return staff
  })
}
const alert = useAlert()

const updateTeam = () => {
  emmit('updateTeam', form.value)
}
</script>

<style lang="scss" module>
.listHeader {
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;

  &__selector {
    display: flex;
    align-items: center;
    height: 39px;
    white-space: nowrap;
    flex-grow: 1;
    justify-content: flex-start;
  }

  &__buttons {
    display: flex;
    white-space: nowrap;
    align-items: center;
    flex-grow: 0;
    gap: 16px;
    justify-content: flex-end;
  }
}

.list {
  display: flex;
  flex-direction: column;
  gap: 4px;

  &:nth-child(n + 2) {
    margin-top: 12px;
  }

  &__item {
    border: 1px solid $color-border-low-emphasis;
    border-radius: $border-radius-v2-base;
    padding: 16px;
  }
}

.listItem {
  align-items: center;
  display: flex;
  justify-content: flex-start;
  gap: 16px;
}

.empty {
  margin-top: 32px;
  margin-bottom: 16px;

  // メンバー追加用セレクターの文章と開始位置を合わせる
  padding-left: 16px;
}
</style>
